import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { initFirebase } from '@paulownia/shared/lib/firebase';
import { useIsSuperAdmin } from '@paulownia/shared/lib/tenant-context';
import { Button } from '@paulownia/shared/components/ui/button';
import { Input } from '@paulownia/shared/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@paulownia/shared/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@paulownia/shared/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@paulownia/shared/components/ui/select';
import { Label } from '@paulownia/shared/components/ui/label';
import { ArrowLeft, Plus, Trash2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { MarketsAdmin } from '@/features/admin/MarketsAdmin';
import { TenantMarketsAdmin } from '@/features/admin/TenantMarketsAdmin';
import { TenantSettingsAdmin } from '@/features/admin/TenantSettingsAdmin';
import { MarketRequestsAdmin } from '@/features/admin/MarketRequestsAdmin';

interface Tenant {
  id: string;
  name: string;
  createdAt?: Date;
}

interface Membership {
  id: string;
  userId: string;
  tenantId: string;
  role: 'admin' | 'staff';
  userEmail?: string;
  userDeleted?: boolean;
}

export const Admin = () => {
  const isSuperAdmin = useIsSuperAdmin();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const [memberships, setMemberships] = useState<Membership[]>([]);

  // Create Tenant form
  const [newTenantName, setNewTenantName] = useState('');
  const [newTenantSlug, setNewTenantSlug] = useState('');
  const [newTenantAdminEmail, setNewTenantAdminEmail] = useState('');

  // Create Membership form
  const [memberEmail, setMemberEmail] = useState('');
  const [memberRole, setMemberRole] = useState<'admin' | 'staff'>('staff');

  useEffect(() => {
    if (isSuperAdmin) {
      loadTenants();
    }
  }, [isSuperAdmin]);

  const loadTenants = async () => {
    setLoading(true);
    try {
      const { functions } = initFirebase();
      const listAllTenants = httpsCallable(functions, 'listAllTenants');
      const result = await listAllTenants();
      const data = result.data as { success: boolean; tenants: Tenant[] };

      if (data.success) {
        setTenants(data.tenants);
      }
    } catch (error) {
      console.error('Error loading tenants:', error);
      toast.error('Erreur lors du chargement des tenants');
    } finally {
      setLoading(false);
    }
  };

  const loadMemberships = async (tenantId: string) => {
    if (!tenantId) return;

    setLoading(true);
    try {
      const { functions } = initFirebase();
      const listTenantMemberships = httpsCallable(functions, 'listTenantMemberships');
      const result = await listTenantMemberships({ tenantId });
      const data = result.data as { success: boolean; memberships: Membership[] };

      if (data.success) {
        // Les emails sont déjà inclus par la Cloud Function
        setMemberships(data.memberships);
      }
    } catch (error) {
      console.error('Error loading memberships:', error);
      toast.error('Erreur lors du chargement des membres');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTenant = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTenantName.trim() || !newTenantSlug.trim() || !newTenantAdminEmail.trim()) return;

    setLoading(true);
    try {
      const { functions } = initFirebase();
      const createTenant = httpsCallable(functions, 'createTenant');
      await createTenant({
        name: newTenantName.trim(),
        slug: newTenantSlug.trim(),
        adminEmail: newTenantAdminEmail.trim()
      });

      toast.success('Tenant créé avec succès');
      setNewTenantName('');
      setNewTenantSlug('');
      setNewTenantAdminEmail('');
      await loadTenants();
    } catch (error) {
      console.error('Error creating tenant:', error);
      toast.error((error as Error).message || 'Erreur lors de la création du tenant');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMembership = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!memberEmail.trim() || !selectedTenantId) return;

    setLoading(true);
    try {
      const { functions } = initFirebase();
      const createMembership = httpsCallable(functions, 'createMembership');
      await createMembership({
        userEmail: memberEmail.trim(),
        tenantId: selectedTenantId,
        role: memberRole,
      });

      toast.success('Membre ajouté avec succès');
      setMemberEmail('');
      await loadMemberships(selectedTenantId);
    } catch (error) {
      console.error('Error creating membership:', error);
      toast.error((error as Error).message || 'Erreur lors de l\'ajout du membre');
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeMembership = async (membershipId: string) => {
    if (!confirm('Êtes-vous sûr de vouloir révoquer cet accès ?')) return;

    setLoading(true);
    try {
      const { functions } = initFirebase();
      const revokeMembership = httpsCallable(functions, 'revokeMembership');
      await revokeMembership({ membershipId });

      toast.success('Accès révoqué avec succès');
      await loadMemberships(selectedTenantId);
    } catch (error) {
      console.error('Error revoking membership:', error);
      toast.error((error as Error).message || 'Erreur lors de la révocation');
    } finally {
      setLoading(false);
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-2xl text-center">Accès refusé</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-center text-muted-foreground">
              Vous devez être superadmin pour accéder à cette page.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard')}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour
          </Button>
          <h1 className="text-3xl font-bold">Administration</h1>
        </div>

        <Tabs defaultValue="tenants" className="w-full">
          <TabsList className="grid w-full grid-cols-6 mb-6">
            <TabsTrigger value="tenants">Tenants</TabsTrigger>
            <TabsTrigger value="memberships">Membres</TabsTrigger>
            <TabsTrigger value="settings">Paramètres</TabsTrigger>
            <TabsTrigger value="markets">Marchés</TabsTrigger>
            <TabsTrigger value="tenant-markets">Associations</TabsTrigger>
            <TabsTrigger value="requests">Demandes</TabsTrigger>
          </TabsList>

          {/* Tenants Tab */}
          <TabsContent value="tenants">
            <div className="grid gap-6 md:grid-cols-2">
              {/* Create Tenant */}
              <Card>
                <CardHeader>
                  <CardTitle>Créer un nouveau tenant</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleCreateTenant} className="space-y-4">
                    <div className="space-y-2">
                      <label htmlFor="tenant-name" className="text-sm font-medium">
                        Nom du tenant
                      </label>
                      <Input
                        id="tenant-name"
                        type="text"
                        value={newTenantName}
                        onChange={(e) => setNewTenantName(e.target.value)}
                        placeholder="Mon entreprise"
                        required
                        disabled={loading}
                      />
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="tenant-slug" className="text-sm font-medium">
                        Slug du tenant
                      </label>
                      <Input
                        id="tenant-slug"
                        type="text"
                        value={newTenantSlug}
                        onChange={(e) => setNewTenantSlug(e.target.value)}
                        placeholder="mon-entreprise"
                        required
                        disabled={loading}
                        pattern="[a-z0-9-]+"
                        minLength={3}
                      />
                      <p className="text-xs text-muted-foreground">
                        Lettres minuscules, chiffres et tirets uniquement. Min. 3 caractères.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="tenant-admin-email" className="text-sm font-medium">
                        Email de l'administrateur
                      </label>
                      <Input
                        id="tenant-admin-email"
                        type="email"
                        value={newTenantAdminEmail}
                        onChange={(e) => setNewTenantAdminEmail(e.target.value)}
                        placeholder="admin@exemple.com"
                        required
                        disabled={loading}
                      />
                      <p className="text-xs text-muted-foreground">
                        L'utilisateur avec cet email sera administrateur du tenant.
                      </p>
                    </div>
                    <Button type="submit" className="w-full" disabled={loading}>
                      {loading ? 'Création...' : 'Créer le tenant'}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* List Tenants */}
              <Card>
                <CardHeader>
                  <CardTitle>Liste des tenants</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {tenants.length === 0 ? (
                      <p className="text-sm text-muted-foreground">Aucun tenant</p>
                    ) : (
                      tenants.map((tenant) => (
                        <div
                          key={tenant.id}
                          className="p-3 border rounded-md hover:bg-gray-50 cursor-pointer"
                          onClick={() => {
                            setSelectedTenantId(tenant.id);
                            loadMemberships(tenant.id);
                          }}
                        >
                          <p className="font-medium">{tenant.name}</p>
                          <p className="text-xs text-muted-foreground">ID: {tenant.id}</p>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Memberships Tab */}
          <TabsContent value="memberships">
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold">Gestion des membres</h2>
              </div>

              {/* Tenant Selection */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Sélectionner un tenant</CardTitle>
                </CardHeader>
                <CardContent>
                  <Select
                    value={selectedTenantId}
                    onValueChange={(value) => {
                      setSelectedTenantId(value);
                      loadMemberships(value);
                    }}
                    disabled={loading}
                  >
                    <SelectTrigger className="w-full md:w-96">
                      <SelectValue placeholder="Choisir un tenant..." />
                    </SelectTrigger>
                    <SelectContent>
                      {tenants.map((tenant) => (
                        <SelectItem key={tenant.id} value={tenant.id}>
                          {tenant.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </CardContent>
              </Card>

              {selectedTenantId && (
                <>
                  {/* Add Member Form */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <UserPlus className="h-5 w-5" />
                        Ajouter un membre
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <form onSubmit={handleCreateMembership} className="space-y-4">
                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="member-email">Email</Label>
                            <Input
                              id="member-email"
                              type="email"
                              value={memberEmail}
                              onChange={(e) => setMemberEmail(e.target.value)}
                              placeholder="utilisateur@exemple.com"
                              required
                              disabled={loading}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="member-role">Rôle</Label>
                            <Select
                              value={memberRole}
                              onValueChange={(value) => setMemberRole(value as 'admin' | 'staff')}
                              disabled={loading}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="staff">Staff</SelectItem>
                                <SelectItem value="admin">Admin</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <Button type="submit" disabled={loading || !memberEmail.trim()}>
                          <Plus className="h-4 w-4 mr-2" />
                          Ajouter le membre
                        </Button>
                      </form>
                    </CardContent>
                  </Card>

                  {/* List Members */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">
                        Membres du tenant ({memberships.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {memberships.length === 0 ? (
                        <p className="text-muted-foreground text-center py-4">
                          Aucun membre pour ce tenant
                        </p>
                      ) : (
                        <div className="space-y-3">
                          {memberships.map((membership) => (
                            <div
                              key={membership.id}
                              className={`flex items-center justify-between p-4 border rounded-lg ${membership.userDeleted ? 'bg-red-50 border-red-200' : ''}`}
                            >
                              <div>
                                <p className={`font-medium ${membership.userDeleted ? 'text-red-600' : ''}`}>
                                  {membership.userEmail}
                                  {membership.userDeleted && (
                                    <span className="ml-2 text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">
                                      Utilisateur supprimé
                                    </span>
                                  )}
                                </p>
                                <p className="text-sm text-muted-foreground">
                                  Rôle: {membership.role === 'admin' ? 'Administrateur' : 'Staff'}
                                </p>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleRevokeMembership(membership.id)}
                                disabled={loading}
                                className="text-destructive hover:text-destructive"
                                title={membership.userDeleted ? 'Supprimer le membership orphelin' : 'Révoquer l\'accès'}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </>
              )}
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <TenantSettingsAdmin />
          </TabsContent>

          {/* Markets Tab */}
          <TabsContent value="markets">
            <MarketsAdmin />
          </TabsContent>

          {/* Tenant-Markets Tab */}
          <TabsContent value="tenant-markets">
            <TenantMarketsAdmin />
          </TabsContent>

          {/* Market Requests Tab */}
          <TabsContent value="requests">
            <MarketRequestsAdmin />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
